import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, Plus, Search, X, Zap } from 'lucide-react';
import { Loader } from '@/components/atoms/Loader';
import {
  fusionPokemon,
  getFusionCandidates,
  type OwnedPokemon,
} from '@/services/pokemon';
import { getInventory } from '@/services/user';
import { getMyPension } from '@/services/pension';
import { cdnUrl } from '@/utils/cdn';
import { TYPE_COLORS } from '@/utils/pokemonTypes';
import { useToast } from '@/hooks/useToastContext';

const IV_KEYS: { key: keyof OwnedPokemon; label: string }[] = [
  { key: 'ivHp', label: 'PV' },
  { key: 'ivAtk', label: 'Atk' },
  { key: 'ivDef', label: 'Déf' },
  { key: 'ivSpAtk', label: 'Atk Sp.' },
  { key: 'ivSpDef', label: 'Déf Sp.' },
  { key: 'ivSpe', label: 'Vit.' },
];

// ─── Slot ─────────────────────────────────────────────────────────────────────

interface SlotProps {
  pokemon: OwnedPokemon | null;
  label: string;
  dimmed?: boolean;
  active?: boolean;
  disabled?: boolean;
  isResult?: boolean;
  onClick?: () => void;
  onClear?: () => void;
}

const Slot = ({ pokemon, label, dimmed, active, disabled, isResult, onClick, onClear }: SlotProps) => {
  const filled = pokemon !== null;

  const borderClass = isResult
    ? filled
      ? 'border-amber-400/60 shadow-amber-400/20 shadow-lg'
      : 'border-amber-400/20'
    : active
      ? 'border-accent shadow-accent/20 shadow-lg'
      : filled
        ? 'border-line-mid'
        : 'border-dashed border-line-soft';

  const bgClass = isResult
    ? filled
      ? 'bg-amber-400/10'
      : 'bg-amber-400/5'
    : filled
      ? 'bg-surface'
      : 'bg-surface/50';

  return (
    <div className="flex flex-col items-center gap-2">
      <span className={`text-xs font-semibold uppercase tracking-widest ${isResult ? 'text-amber-400' : 'text-muted'}`}>
        {label}
      </span>

      <div className="relative">
        <button
          onClick={onClick}
          disabled={disabled && !filled}
          className={`
            relative flex h-28 w-28 items-center justify-center rounded-2xl border-2 transition-all duration-200
            ${borderClass} ${bgClass}
            ${dimmed ? 'opacity-50' : ''}
            ${disabled && !filled ? 'cursor-not-allowed' : onClick ? 'cursor-pointer hover:scale-[1.03]' : ''}
          `}
        >
          {filled ? (
            <div className="flex flex-col items-center gap-1 p-1">
              <img
                src={cdnUrl(pokemon!.isShiny ? pokemon!.pokemon.spriteShiny : pokemon!.pokemon.sprite)}
                alt={pokemon!.nickname}
                className="h-16 w-16 object-contain drop-shadow-sm"
                onError={(e) => { e.currentTarget.style.opacity = '0'; }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1 opacity-40">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-raised">
                <Plus size={18} className="text-muted" />
              </div>
            </div>
          )}

          {isResult && filled && (
            <div className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400">
              <Zap size={10} className="text-white" />
            </div>
          )}
        </button>

        {filled && onClear && (
          <button
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-surface border border-line-soft text-muted hover:text-content transition-colors"
          >
            <X size={10} />
          </button>
        )}
      </div>

      {filled ? (
        <div className="text-center">
          <p className="font-lexend text-content max-w-[7rem] truncate text-xs font-semibold">
            {pokemon!.nickname}
          </p>
          <p className="text-muted text-[10px]">{pokemon!.pokemon.realname}</p>
          {!isResult && (
            <div className="mt-1 flex flex-wrap justify-center gap-0.5">
              {pokemon!.pokemon.types.map((t) => (
                <span
                  key={t}
                  className={`${TYPE_COLORS[t] ?? 'bg-gray-400'} rounded px-1 py-0.5 text-[9px] font-semibold text-white capitalize`}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-muted text-[11px]">{disabled ? 'Choisir source d\'abord' : 'Cliquer pour choisir'}</p>
      )}
    </div>
  );
};

// ─── Stat diff table ───────────────────────────────────────────────────────────

const StatTable = ({ source, target }: { source: OwnedPokemon; target: OwnedPokemon }) => {
  const rows = [
    { label: 'Niveau', src: source.level, tgt: target.level, result: Math.max(source.level, target.level) },
    ...IV_KEYS.map(({ key, label }) => ({
      label,
      src: source[key] as number,
      tgt: target[key] as number,
      result: Math.max(source[key] as number, target[key] as number),
    })),
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-line-soft bg-surface">
      <div className="grid grid-cols-4 border-b border-line-soft px-4 py-2">
        <span className="text-muted text-xs font-semibold uppercase">Stat</span>
        <span className="text-muted text-center text-xs font-semibold uppercase">Source</span>
        <span className="text-muted text-center text-xs font-semibold uppercase">Cible</span>
        <span className="text-center text-xs font-semibold uppercase text-amber-400">Résultat</span>
      </div>
      {rows.map((row) => {
        const improved = row.result > row.src;
        return (
          <div
            key={row.label}
            className="grid grid-cols-4 items-center border-b border-line-soft/50 px-4 py-2 last:border-0"
          >
            <span className="text-content/70 text-xs">{row.label}</span>
            <span className="text-content text-center text-sm font-semibold tabular-nums">{row.src}</span>
            <span className="text-content/50 text-center text-sm tabular-nums">{row.tgt}</span>
            <span className={`text-center text-sm font-semibold tabular-nums ${improved ? 'text-green-500' : 'text-content'}`}>
              {row.result}
              {improved && <span className="ml-0.5 text-[10px]">↑</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────────

type PickingSlot = 'source' | 'target' | null;

export const Fusion = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();

  const preselected = (location.state as { sourcePokemon?: OwnedPokemon } | null)?.sourcePokemon ?? null;

  const [source, setSource] = useState<OwnedPokemon | null>(preselected);
  const [target, setTarget] = useState<OwnedPokemon | null>(null);
  const [pickingSlot, setPickingSlot] = useState<PickingSlot>(preselected ? null : null);

  const { data: inventoryData } = useQuery({
    queryKey: ['inventory-fusion'],
    queryFn: () => getInventory({ limit: 200 }),
    staleTime: 60_000,
  });
  const crystalCount = inventoryData?.data.find((item) => item.itemId === 35)?.quantity ?? 0;

  const { data: myPension } = useQuery({
    queryKey: ['myPension'],
    queryFn: getMyPension,
    staleTime: 30_000,
  });
  const pensionPokemonId = myPension?.pokemon.ownedPokemonId ?? null;

  const [sourceSearch, setSourceSearch] = useState('');

  const { data: candidatesData, isLoading: isLoadingAll } = useQuery({
    queryKey: ['fusion-candidates'],
    queryFn: getFusionCandidates,
    enabled: pickingSlot === 'source' || pickingSlot === 'target',
    staleTime: 30_000,
  });

  // Only species with ≥ 2 eligible Pokémon (no held item, not in pension, not KO)
  // The back-end already filters to species with ≥ 2 copies, but we still need to
  // apply the eligibility filter and re-check the ≥ 2 constraint after it.
  const eligibleSources = useMemo(() => {
    const all = candidatesData?.data ?? [];
    const eligible = all.filter(
      (p) => p.heldItemId === null && p.hp > 0 && p.ownedPokemonId !== pensionPokemonId,
    );
    const bySpecies = eligible.reduce<Record<string, OwnedPokemon[]>>((acc, p) => {
      (acc[String(p.pokemonId)] ??= []).push(p);
      return acc;
    }, {});
    const sources: OwnedPokemon[] = [];
    Object.values(bySpecies).forEach((group) => {
      if (group.length >= 2) sources.push(...group);
    });
    const q = sourceSearch.toLowerCase();
    return q
      ? sources.filter(
          (p) =>
            p.nickname.toLowerCase().includes(q) ||
            p.pokemon.realname.toLowerCase().includes(q),
        )
      : sources;
  }, [candidatesData, pensionPokemonId, sourceSearch]);

  const isLoadingTargets = isLoadingAll;

  const eligibleTargets = (candidatesData?.data ?? []).filter(
    (p) =>
      p.pokemonId === source?.pokemonId &&
      p.ownedPokemonId !== source?.ownedPokemonId &&
      p.heldItemId === null &&
      p.ownedPokemonId !== pensionPokemonId,
  );

  const { mutate: doFusion, isPending: isFusing } = useMutation({
    mutationFn: () => fusionPokemon(source!.ownedPokemonId, target!.ownedPokemonId),
    onSuccess: () => {
      showToast('success', 'Fusion réussie !', 5000);
      queryClient.invalidateQueries({ queryKey: ['pokemons'] });
      queryClient.invalidateQueries({ queryKey: ['fusion-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-fusion'] });
      setSource(null);
      setTarget(null);
      setPickingSlot(null);
    },
    onError: (err: Error) => {
      const m = err.message.toLowerCase();
      if (m.includes('espèce')) showToast('error', 'Les deux Pokémon doivent être de la même espèce.', 5000);
      else if (m.includes('item') || m.includes('tenu')) showToast('error', "Un Pokémon porte un item. Retirez-le d'abord.", 5000);
      else if (m.includes('pension')) showToast('error', 'Un Pokémon est à la pension.', 5000);
      else if (m.includes('cristal')) showToast('error', "Vous n'avez plus de Cristal de Fusion.", 5000);
      else showToast('error', err.message, 5000);
    },
  });

  const resultLevel = source && target ? Math.max(source.level, target.level) : null;
  const canFuse = source !== null && target !== null && crystalCount > 0 && !isFusing;

  const openSlot = (slot: PickingSlot) => {
    setPickingSlot((prev) => (prev === slot ? null : slot));
  };

  return (
    <main className="mx-auto max-w-2xl space-y-5 px-4 py-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="https://cdn.bitume2000.fr/items/35.png" alt="Cristal" className="h-6 w-6 object-contain" />
          <span className="font-lexend text-content text-sm font-semibold">
            {crystalCount} Cristal{crystalCount !== 1 ? 's' : ''} de Fusion
          </span>
        </div>
        {crystalCount === 0 && (
          <div className="flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1">
            <AlertTriangle size={12} className="text-amber-500" />
            <span className="text-xs text-amber-600 dark:text-amber-400">Aucun cristal</span>
          </div>
        )}
      </div>

      {/* Anvil zone */}
      <div className="rounded-3xl border border-line-soft bg-surface-raised p-6">
        <div className="flex items-start justify-center gap-3 sm:gap-6">

          {/* Source slot */}
          <Slot
            pokemon={source}
            label="Source"
            active={pickingSlot === 'source'}
            onClick={() => openSlot('source')}
            onClear={() => { setSource(null); setTarget(null); setPickingSlot(null); }}
          />

          {/* + */}
          <div className="mt-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface text-muted">
            <Plus size={14} />
          </div>

          {/* Target slot */}
          <Slot
            pokemon={target}
            label="Cible"
            active={pickingSlot === 'target'}
            disabled={!source}
            onClick={source ? () => openSlot('target') : undefined}
            onClear={() => { setTarget(null); setPickingSlot(null); }}
            dimmed={!source}
          />

          {/* → */}
          <div className="mt-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface">
            <span className="text-muted text-sm">→</span>
          </div>

          {/* Result slot */}
          <Slot
            pokemon={source && target ? source : null}
            label="Résultat"
            isResult
          />
        </div>

        {/* Result level pill */}
        {source && target && (
          <p className="text-muted mt-4 text-center text-xs">
            <span className="text-content font-semibold">{source.nickname}</span> — Niv.&nbsp;
            <span className="text-amber-400 font-semibold">{resultLevel}</span>
          </p>
        )}
      </div>

      {/* Picker panel */}
      {pickingSlot === 'source' && (
        <div className="rounded-2xl border border-line-soft bg-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="font-lexend text-content text-sm font-semibold">Choisir le Pokémon source</p>
              <p className="text-muted text-xs">Espèces dont vous avez au moins un doublon.</p>
            </div>
            <button onClick={() => setPickingSlot(null)} className="text-muted hover:text-content">
              <ChevronDown size={16} />
            </button>
          </div>

          <div className="relative mb-3">
            <Search size={13} className="text-content/40 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2" />
            <input
              type="text"
              value={sourceSearch}
              onChange={(e) => setSourceSearch(e.target.value)}
              placeholder="Rechercher..."
              className="text-content placeholder:text-content/40 h-9 w-full rounded-xl border border-line-soft bg-raised pl-8 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            {sourceSearch && (
              <button
                type="button"
                onClick={() => setSourceSearch('')}
                className="text-content/40 hover:text-content/70 absolute top-1/2 right-2.5 -translate-y-1/2"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {isLoadingAll ? (
            <div className="flex justify-center py-10">
              <Loader size={32} color="#f29602" />
            </div>
          ) : eligibleSources.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-10">
              <p className="text-4xl">🔍</p>
              <p className="text-content/60 text-sm text-center">
                {sourceSearch
                  ? `Aucun résultat pour « ${sourceSearch} »`
                  : 'Aucun Pokémon fusionnable. Vous devez posséder au moins deux exemplaires de la même espèce.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {eligibleSources.map((p) => (
                <button
                  key={p.ownedPokemonId}
                  onClick={() => { setSource(p); setTarget(null); setPickingSlot(null); }}
                  className={`flex flex-col items-center gap-1 rounded-xl border p-2 text-center transition-all hover:border-accent hover:bg-surface-raised ${
                    source?.ownedPokemonId === p.ownedPokemonId
                      ? 'border-accent bg-surface-raised'
                      : 'border-line-soft bg-surface'
                  }`}
                >
                  <div className="flex h-12 w-12 items-center justify-center">
                    <img
                      src={cdnUrl(p.isShiny ? p.pokemon.spriteShiny : p.pokemon.sprite)}
                      alt={p.nickname}
                      className="h-11 w-11 object-contain"
                      onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                    />
                  </div>
                  <p className="font-lexend text-content w-full truncate text-[11px] font-semibold">{p.nickname}</p>
                  <span className="text-muted text-[10px]">Nv.{p.level}</span>
                  {p.isShiny && <span className="text-[10px]">✨</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {pickingSlot === 'target' && source && (
        <div className="rounded-2xl border border-line-soft bg-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="font-lexend text-content text-sm font-semibold">
                Choisir le {source.pokemon.realname} cible
              </p>
              <p className="text-muted text-xs">Il sera détruit définitivement.</p>
            </div>
            <button onClick={() => setPickingSlot(null)} className="text-muted hover:text-content">
              <ChevronDown size={16} />
            </button>
          </div>

          {isLoadingTargets ? (
            <div className="flex justify-center py-10">
              <Loader size={32} color="#f29602" />
            </div>
          ) : eligibleTargets.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-10">
              <p className="text-4xl">🔍</p>
              <p className="text-content/60 text-sm">
                Aucun autre {source.pokemon.realname} disponible dans votre PC.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {eligibleTargets.map((p) => (
                <button
                  key={p.ownedPokemonId}
                  onClick={() => { setTarget(p); setPickingSlot(null); }}
                  className={`flex flex-col items-center gap-1 rounded-xl border p-2 text-center transition-all hover:border-accent hover:bg-surface-raised ${
                    target?.ownedPokemonId === p.ownedPokemonId
                      ? 'border-accent bg-surface-raised'
                      : 'border-line-soft bg-surface'
                  }`}
                >
                  <div className="flex h-12 w-12 items-center justify-center">
                    <img
                      src={cdnUrl(p.isShiny ? p.pokemon.spriteShiny : p.pokemon.sprite)}
                      alt={p.nickname}
                      className="h-11 w-11 object-contain"
                      onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                    />
                  </div>
                  <p className="font-lexend text-content w-full truncate text-[11px] font-semibold">{p.nickname}</p>
                  <span className="text-muted text-[10px]">Nv.{p.level}</span>
                  {p.isShiny && <span className="text-[10px]">✨</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stat comparison */}
      {source && target && (
        <>
          <StatTable source={source} target={target} />

          <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-red-500" />
            <p className="text-sm text-red-600 dark:text-red-400">
              <strong>{target.nickname}</strong> sera <strong>supprimé définitivement</strong>.
            </p>
          </div>

          <button
            onClick={() => doFusion()}
            disabled={!canFuse}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-400 px-6 py-3.5 font-lexend text-sm font-semibold text-white shadow-md shadow-amber-400/30 transition-all hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isFusing ? (
              <Loader size={18} color="white" />
            ) : (
              <>
                <img src="https://cdn.bitume2000.fr/items/35.png" alt="" className="h-5 w-5 object-contain" />
                Fusionner
                <Zap size={14} />
              </>
            )}
          </button>
        </>
      )}
    </main>
  );
};
